import { computed, onBeforeUnmount, ref, shallowRef, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import type { PsiData } from '../api/psi';
import type { FeedStatus } from '../api/freshness';
import type { Pun } from '../data/puns';
import {
  captureShareSnapshot,
  selectShareReading,
  shareCaption,
  shareFilename,
  type ShareSnapshot,
  type ShareRegion,
  type ShareFormat,
} from '../services/share-snapshot';
import { shareTargets } from '../services/share-targets';

interface ShareReading {
  previousDesign: Readonly<Ref<boolean>>;
  liveData: Readonly<Ref<PsiData | null>>;
  feedStatus: Readonly<Ref<FeedStatus>>;
  currentPun: Readonly<Ref<Pun>>;
  shareModalOpen: Ref<boolean>;
}
export function useSharing({
  previousDesign,
  liveData,
  feedStatus,
  currentPun,
  shareModalOpen,
}: ShareReading) {
  const route = useRoute();
  const canNativeShare = typeof navigator.share === 'function';
  const snapshot = shallowRef<ShareSnapshot | null>(null);
  const shareRegion = ref<ShareRegion>('national');
  const shareFormat = ref<ShareFormat>('portrait');
  const shareCardUrl = ref('');
  const shareCardBlob = shallowRef<Blob | null>(null);
  const sharePreparing = ref(false);
  const shareError = ref('');
  const nativeSharePending = ref(false);
  const toastMessage = ref('');
  const toastVisible = ref(false);
  const shareData = computed(() =>
    snapshot.value ? selectShareReading(snapshot.value, shareRegion.value) : null,
  );
  const caption = computed(() =>
    snapshot.value && shareData.value ? shareCaption(snapshot.value, shareData.value) : '',
  );
  const currentShareTargets = computed(() =>
    snapshot.value
      ? shareTargets(
          snapshot.value.url,
          caption.value.replace(`\n\nLive Singapore PSI & puns: ${snapshot.value.url}`, ''),
        )
      : [],
  );
  const shareCardFilename = computed(() =>
    shareData.value
      ? shareFilename(shareData.value, shareRegion.value, shareFormat.value)
      : 'hazepun.png',
  );
  const shareRegionOptions = computed(() => [
    { value: 'national' as ShareRegion, label: 'Singapore · highest PSI', disabled: false },
    ...(snapshot.value?.reading.regions ?? []).map((region) => ({
      value: region.region,
      label: region.region[0].toUpperCase() + region.region.slice(1),
      disabled: region.psi === null,
    })),
  ]);
  let toastTimer = 0;
  let cardRequest = 0;
  function releaseCard() {
    if (shareCardUrl.value) URL.revokeObjectURL(shareCardUrl.value);
    shareCardUrl.value = '';
    shareCardBlob.value = null;
  }
  function showToast(message: string) {
    toastMessage.value = message;
    toastVisible.value = true;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toastVisible.value = false;
    }, 2600);
  }
  async function renderShareCard() {
    const captured = snapshot.value;
    const data = shareData.value;
    if (!captured || !data || !shareModalOpen.value) return;
    const request = ++cardRequest;
    const format = shareFormat.value;
    sharePreparing.value = true;
    shareError.value = '';
    try {
      const { generateShareCard } = await import('../services/share-card');
      const blob = await generateShareCard(data, captured.previousDesign, format);
      if (request !== cardRequest) return;
      releaseCard();
      shareCardBlob.value = blob;
      shareCardUrl.value = URL.createObjectURL(blob);
    } catch {
      if (request === cardRequest) {
        releaseCard();
        shareError.value = 'Could not make the share card. Please try again.';
      }
    } finally {
      if (request === cardRequest) sharePreparing.value = false;
    }
  }
  function openShareCardModal() {
    if (!liveData.value) return;
    snapshot.value = captureShareSnapshot(
      liveData.value,
      currentPun.value,
      window.location.origin + route.path,
      previousDesign.value,
      feedStatus.value,
    );
    shareRegion.value = 'national';
    shareFormat.value = 'portrait';
    releaseCard();
    shareError.value = '';
    sharePreparing.value = true;
    shareModalOpen.value = true;
  }
  watch([snapshot, shareModalOpen, shareRegion, shareFormat], () => {
    if (shareModalOpen.value) void renderShareCard();
  });
  watch(shareModalOpen, (open) => {
    if (!open) {
      cardRequest++;
      sharePreparing.value = false;
    }
  });
  watch(
    () => route.fullPath,
    () => {
      cardRequest++;
      shareModalOpen.value = false;
      releaseCard();
    },
  );
  async function triggerNativeShare() {
    const captured = snapshot.value;
    const data = shareData.value;
    if (!captured || !data || !canNativeShare || sharePreparing.value || nativeSharePending.value)
      return;
    const payload: ShareData = {
      title: `HazePun · PSI ${data.psi} (${data.tier.name})`,
      text: caption.value,
      url: captured.url,
    };
    if (shareCardBlob.value && navigator.canShare) {
      const file = new File([shareCardBlob.value], shareCardFilename.value, { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) payload.files = [file];
    }
    nativeSharePending.value = true;
    try {
      await navigator.share(payload);
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError'))
        showToast('Could not open the share window.');
    } finally {
      nativeSharePending.value = false;
    }
  }
  async function copyShareText() {
    if (!caption.value) return;
    if (!navigator.clipboard) {
      showToast('Clipboard is unavailable in this browser.');
      return;
    }
    try {
      await navigator.clipboard.writeText(caption.value);
      showToast('Caption copied! Go forth and groan.');
    } catch {
      showToast('Clipboard access was denied.');
    }
  }
  onBeforeUnmount(() => {
    cardRequest++;
    releaseCard();
    window.clearTimeout(toastTimer);
  });
  return {
    canNativeShare,
    shareModalOpen,
    shareCardUrl,
    shareCardFilename,
    shareRegion,
    shareFormat,
    shareRegionOptions,
    sharePreparing,
    shareError,
    shareData,
    nativeSharePending,
    currentShareTargets,
    toastMessage,
    toastVisible,
    openShareCardModal,
    triggerNativeShare,
    copyShareText,
    renderShareCard,
  };
}
